import * as Dialog from "@radix-ui/react-dialog";
import {
  formatPlainDate,
  parsePlainDate,
  PlainDate,
  type PlainDate as PlainDateType,
  type PlainDateString,
} from "@sa-nu/shared-utils/date";
import { formatCurrency } from "@sa-nu/shared-utils/price";
import clsx from "clsx";
import Image from "next/image";
import { useCallback, useMemo, useState } from "react";

import type { AccommodationFacilityType } from "../../../../__generated__/gql/graphql";
import { useGtm } from "../../../../lib/google-tag-manager/gtm";
import type { AccommodationNoticeForUser } from "../../accommodation/accommodation-notices";
import { AccommodationNotices } from "../../accommodation/accommodation-notices";
import CloseIcon from "../../icons/close-icon";

import {
  BlockHoverCardWrapper,
  type BlockWithUser,
} from "./areas/cell/block-hover-card-wrapper";
import type { SelectedAccommodationReservation } from "./reservation-calendar";

export type ReservationCyclePeriod = {
  start: PlainDateString;
  end: PlainDateString;
  stayedAndReservedNights: number;
};

type DialogProps = {
  firstNight: PlainDateType;
  selectList: {
    date: PlainDateType;
    canReserve: boolean;
    additionalNightsFeeOfCycle: number;
    discounts: {
      ruleType: "NightFeeModifier" | "ConsecutiveNightDiscount" | "FreeNight";
      feeModifier: number;
      feeModifierType: "Value" | "Percentage";
    }[];
    block: BlockWithUser | null | undefined;
  }[];
  reservationCyclePeriods: readonly ReservationCyclePeriod[];
  providerTypename: string;
  accommodationId: string;
  accommodationName: string;
  accommodationFacilities: AccommodationFacilityType[];
  accommodationNotices: AccommodationNoticeForUser[];
};

export function useCheckOutDateSelectionModal({
  onSelect,
}: {
  onSelect: (
    selected: Omit<SelectedAccommodationReservation, "accommodationFacilities">,
    accommodationFacilities: AccommodationFacilityType[],
  ) => void;
}) {
  const [state, setState] = useState<DialogProps | null>(null);

  const openDialog = useCallback((state: DialogProps) => {
    setState(state);
  }, []);

  const renderDialog = useCallback(() => {
    if (state == null) return null;
    return (
      <CheckOutDateSelectionModal
        firstNight={state.firstNight}
        selectList={state.selectList}
        reservationCyclePeriods={state.reservationCyclePeriods}
        providerTypename={state.providerTypename}
        accommodationId={state.accommodationId}
        accommodationName={state.accommodationName}
        accommodationNotices={state.accommodationNotices}
        setSelected={(value) => {
          onSelect(value, state.accommodationFacilities);
        }}
        setClose={() => setState(null)}
      />
    );
  }, [state, onSelect]);

  return { renderDialog, openDialog };
}

function CheckOutDateSelectionModal({
  firstNight,
  selectList,
  reservationCyclePeriods,
  providerTypename,
  accommodationId,
  accommodationName,
  accommodationNotices,
  setSelected,
  setClose,
}: {
  firstNight: PlainDateType;
  selectList: {
    date: PlainDateType;
    canReserve: boolean;
    additionalNightsFeeOfCycle: number;
    discounts: {
      ruleType: "NightFeeModifier" | "ConsecutiveNightDiscount" | "FreeNight";
      feeModifier: number;
      feeModifierType: "Value" | "Percentage";
    }[];
    block: BlockWithUser | null | undefined;
  }[];
  reservationCyclePeriods: readonly ReservationCyclePeriod[];
  providerTypename: string;
  accommodationId: string;
  accommodationName: string;
  accommodationNotices: AccommodationNoticeForUser[];
  setSelected: (
    selected: Omit<SelectedAccommodationReservation, "accommodationFacilities">,
  ) => void;
  setClose: () => void;
}) {
  const gtm = useGtm();

  const lastReservableDate = useMemo(() => {
    return selectList
      .slice()
      .reverse()
      .find((item) => item.canReserve)?.date;
  }, [selectList]);

  const { currentCycleNights } = useMemo(
    () =>
      getCurrentCycleNights({
        firstNight,
        reservationCyclePeriods,
        providerTypename,
      }),
    [firstNight, reservationCyclePeriods, providerTypename],
  );
  return (
    <Dialog.Root
      defaultOpen
      onOpenChange={(open) => {
        if (!open) setClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="sd-overlay">
          <Dialog.Close className="sd-close">
            <CloseIcon />
          </Dialog.Close>
          <Dialog.Content aria-describedby={undefined}>
            <div className="sd-content mb-0! w-[315px]!">
              <Dialog.Title className="sd-title-header">
                チェックアウト日を選択
              </Dialog.Title>
              <div className="p-[30px]">
                <div>
                  <div className="mb-[5px] text-[10px] text-[rgba(0,0,0,0.54)]">
                    施設
                  </div>
                  <div className="text-base">{accommodationName}</div>
                </div>
                <div className="pt-[25px]">
                  <div className="mb-[5px] text-[10px] text-[rgba(0,0,0,0.54)]">
                    チェックイン
                  </div>
                  <div className="text-base">
                    {formatPlainDate(firstNight, {
                      withDayOfWeek: true,
                    })}
                  </div>
                </div>
                <div className="pt-[25px] pb-[5px]">
                  <div className="mb-[7px] text-[10px] text-[rgba(0,0,0,0.54)]">
                    チェックアウト
                  </div>
                  {selectList.map((selectItem, i) => (
                    <button
                      data-analytics-id="reservation_calendar_selected_check_out_date"
                      key={i}
                      className={clsx(
                        "border-sbeige mb-[10px] flex w-full flex-col items-center border py-[8px] last:mb-0 hover:enabled:bg-[#f7f7f7] disabled:bg-[#c8cac7]",
                        {
                          "border-sorange relative border-dashed":
                            !!selectItem.block,
                        },
                      )}
                      disabled={!selectItem.canReserve}
                      onClick={() => {
                        if (!selectItem) return;
                        gtm.sendEvent("rsv_cal_checkout_selected");
                        setSelected({
                          accommodationId: accommodationId,
                          firstNight: firstNight,
                          lastNight: selectItem.date.subtract({
                            days: 1,
                          }),
                        });
                        setClose();
                      }}
                    >
                      <div className="flex items-center justify-center">
                        <div>
                          {formatPlainDate(selectItem.date, {
                            excludeYear: true,
                            withDayOfWeek: true,
                          })}
                        </div>
                        <div className="ml-[14px] flex flex-col items-center">
                          <div className="border-sorange text-sorange h-[22px] w-[62px] rounded-[3px] border text-center text-[13px] font-medium">
                            {i + 1}泊
                          </div>
                          {selectItem.discounts.length > 0 && (
                            <div className="mt-[4px] flex flex-col items-center">
                              {selectItem.discounts.map((discount, idx) => {
                                if (
                                  discount.ruleType ===
                                  "ConsecutiveNightDiscount"
                                ) {
                                  return (
                                    <div
                                      key={idx}
                                      className="font-roboto text-skogecha text-[10px]"
                                    >
                                      連泊割 {Math.abs(discount.feeModifier)}
                                      %OFF
                                    </div>
                                  );
                                }
                                return null;
                              })}
                            </div>
                          )}
                        </div>
                        {selectItem.additionalNightsFeeOfCycle > 0 && (
                          <div className="font-roboto ml-[10px] text-[10px]">
                            +{" "}
                            {formatCurrency(
                              selectItem.additionalNightsFeeOfCycle,
                            )}
                          </div>
                        )}
                      </div>
                      {selectItem.block && (
                        <BlockHoverCardWrapper block={selectItem.block}>
                          <div className="absolute right-[10px] bottom-[14px] h-[12px] w-[12px]">
                            <Image
                              src={"/i/snackbar_icon_alert.svg"}
                              alt="success"
                              width={16}
                              height={16}
                            />
                          </div>
                        </BlockHoverCardWrapper>
                      )}
                    </button>
                  ))}
                </div>
                {currentCycleNights !== null && (
                  <div className="pt-[15px]">
                    <div className="text-[11px]">
                      現サイクル内 宿泊・予約日数： {currentCycleNights} 泊
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="w-[315px]! pt-[15px]">
              <AccommodationNotices
                notices={accommodationNotices}
                firstDate={firstNight}
                lastDate={lastReservableDate ?? firstNight}
              />
            </div>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function getCurrentCycleNights({
  firstNight,
  reservationCyclePeriods,
  providerTypename,
}: {
  firstNight: PlainDateType;
  reservationCyclePeriods: readonly ReservationCyclePeriod[];
  providerTypename: string;
}): { currentCycleNights: number | null } {
  if (
    providerTypename !== "StandardPlanAccommodationReservationProvider" ||
    reservationCyclePeriods.length === 0
  ) {
    return { currentCycleNights: null };
  }

  const targetCycle = reservationCyclePeriods.find(
    (cycle) =>
      PlainDate.compare(firstNight, parsePlainDate(cycle.start)) >= 0 &&
      PlainDate.compare(firstNight, parsePlainDate(cycle.end)) <= 0,
  );
  const currentCycleNights = targetCycle?.stayedAndReservedNights ?? null;

  return { currentCycleNights };
}
